import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IScatters, ISpinOutcome, ISpinStep } from '@GFTypes/GameStoreTypes/SpinOutcome';
import { IActionResponseData, IActionResponseOutcome, IActionResponseStep } from '@GFTypes/Responses/ActionResponse';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { processCollectorData } from '../Data/CollectorDataProcessor';
import { createDoubleCoinMap, getSymbolIdWithDoubleCoinRole, isDoubleLockAndRollCoinPresent } from '../Data/DoubleLockAndRollCoinDataProcessor';
import { getSpecialGames, getStepsWithSpecialSymbolWinningLines } from '../Data/SpecialGamesDataProcessor';
import { getCorrectWallMultiplier, getPowerLinesForWallWin, getWinLinesForWallWin } from '../Data/WallWinDataProcessor';
import { processScatters, processWays, processWinningLines } from '../Data/WinDataProcessor';
import { getVisibleSymbolsFromFinalSymbols, mergeDoubleReels, normalizeDoubleReels, normalizeStartOrFinalSymbols } from '../Utils/SymbolIndexNormalizerUtils';
import { buildSelectionMaskFromPositions } from '../Utils/Utils';
export interface IStepExtension {
    visibleSymbols: number[][];
}

export const processSpinResponse = (data: IActionResponseData): ISpinOutcome => {
    if (data?.error) throw new Error('Spin request error: ' + data.error);

    const clone = structuredClone(Array.isArray(data.result.outcome) ? data.result.outcome[0] : data.result.outcome);
    const isPowerLines = gameInstance.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).powerLines;
    const isGameWithSpecialSymbols = !!gameInstance.gameStore.get(EGameStoreKeys.Features).specialGames?.[ESpecialGamesType.FREE_GAMES]?.[
        ESymbolRoles.SPECIAL_SYMBOL
    ];

    let processedSteps = processSteps(clone, isPowerLines, isGameWithSpecialSymbols);

    if (clone.specialGames) {
        processedSteps = getStepsWithSpecialSymbolWinningLines(processedSteps, clone.gameType);
    }

    return {
        ...clone,
        limitReached: clone.limitReached ?? clone.limitreached, //TODO: Delete it after F2-1315 is done by P1
        steps: processedSteps,
        specialGames: getSpecialGames(clone)
    };
};

function processSteps(outcome: IActionResponseOutcome, isPowerLines: boolean, isGameWithSpecialSymbols: boolean): ISpinStep[] {
    if (!isGameWithSpecialSymbols && outcome.steps.length > 1) {
        const firstStep = outcome.steps?.at(0);
        const lastStep = outcome.steps?.at(-1);
        if (firstStep && lastStep) {
            lastStep.scatters = firstStep.scatters;
            firstStep.scatters = undefined;
        }
    }
    const authGameConfig = gameInstance.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
    const doubleReels = isDoubleLockAndRollCoinPresent(authGameConfig);
    const firstDoubleSymbolId = getSymbolIdWithDoubleCoinRole(authGameConfig);
    const doubleCoinMap = doubleReels ? createDoubleCoinMap(firstDoubleSymbolId) : new Map();

    return outcome.steps.map((step) => {
        const finalSymbolsWithDoubleReels = doubleReels ? normalizeDoubleReels(step.finalSymbols, doubleCoinMap) : undefined;
        const isDuringSpecialGame = outcome.specialGames?.currentSpin && outcome.specialGames?.currentSpin > 0;
        const finalSymbolsWithMergedReels = doubleReels && isDuringSpecialGame ? mergeDoubleReels(step.finalSymbols, firstDoubleSymbolId) : undefined;
        const potentialWinningSymbols =
            doubleReels && !isDuringSpecialGame ? step.potentialWinningSymbols?.filter((_, index) => index % 2 === 0) : step.potentialWinningSymbols;
        const finalSymbols = normalizeStartOrFinalSymbols(step.finalSymbols);
        const visibleSymbols = getVisibleSymbolsFromFinalSymbols(finalSymbols);
        if (!step.winMultiplier) step.winMultiplier = outcome.steps[0].winMultiplier;
        const totalWinMultiplier = getTotalMultiplier(step.winMultiplier, step.winMultipliers);
        const wallMultiplier = getCorrectWallMultiplier(step.wallMultiplier, visibleSymbols[0][0]);
        const stepExtension: IStepExtension = { visibleSymbols: visibleSymbols };
        const defaultCollectors = authGameConfig.defaultCollectors;

        let powerLines = processWays(step, stepExtension);
        let winningLines = processWinningLines(step, stepExtension);
        let scatters = processScatters(step, stepExtension);
        let collectors = step.collectors ? processCollectorData(step.collectors, defaultCollectors) : undefined;

        if (step.wallMultiplier) {
            if (isPowerLines) powerLines = getPowerLinesForWallWin(step, stepExtension, wallMultiplier);
            else winningLines = getWinLinesForWallWin(step, stepExtension, wallMultiplier);
        }

        const bonusWheelScatters = step.bonusWheelMultiplier && !step.scatters ? getScattersForBonusWheel(step, stepExtension) : undefined;

        return {
            ...step,
            finalSymbols,
            finalSymbolsWithDoubleReels,
            finalSymbolsWithMergedReels,
            finalVisibleSymbols: visibleSymbols,
            scatters: bonusWheelScatters ?? scatters,
            totalWinMultiplier,
            wallMultiplier,
            powerLines,
            winningLines,
            collectors,
            potentialWinningSymbols
        };
    });
}

export const getTotalMultiplier = (multiplier?: number, multipliers?: number[]): number | undefined => {
    if (multiplier) return multiplier;

    if (multipliers) return multipliers.reduce((acc, current) => acc * current, 1);

    return undefined;
};

export const getScattersForBonusWheel = (step: IActionResponseStep, stepExtension: IStepExtension): IScatters[] => {
    const scatters: IScatters = { positions: [], symbolId: -1, win: 0, selectionMask: [], winMessageMultiplier: 1 };
    stepExtension.visibleSymbols.forEach((reel, reelIndex) => {
        reel.forEach((symbolId, symbolIndex) => {
            if (getSymbolRoles(gameInstance.gameStore, symbolId).includes(ESymbolRoles.BONUS_WHEEL_SCATTER)) {
                scatters.symbolId = symbolId;
                scatters.positions!.push([reelIndex, symbolIndex]);
            }
        });
    });
    scatters.selectionMask = buildSelectionMaskFromPositions(scatters.positions, stepExtension.visibleSymbols);
    scatters.win = step.bonusWheelMultiplier! * gameInstance.gameStore.get(EGameStoreKeys.LastSpinBet)!;

    return [scatters];
};
